// src/engine/NativeEngineClient.ts
// Client for the native Rust chess engine HTTP API
// Enables 20-thread parallel search from the frontend

import { Position, Piece, PieceColor } from "../types/Board";

// ============================================================================
// API Types
// ============================================================================

interface SearchRequest {
  fen: string;
  depth?: number;
  time_ms?: number;
  threads?: number;
  priority_order?: string[];
  // Configurable evaluation weights from AI Settings (0.0 - 1.0)
  material_weight?: number;
  position_weight?: number;
  king_safety_weight?: number;
  mobility_weight?: number;
  pawn_structure?: boolean;
  board_control?: boolean;
  // Tertiary settings - Engine internals
  quiescence_depth?: number;
  aspiration_window?: number;
  lmr_threshold?: number;
  null_move_r?: number;
  tt_size_mb?: number;
}

// Evaluation weights interface for frontend use
export interface EvaluationWeights {
  material: number;      // 0.0 - 1.0, default 1.0
  position: number;      // 0.0 - 1.0, default 1.0
  kingSafety: number;    // 0.0 - 1.0, default 1.0
  mobility: number;      // 0.0 - 1.0, default 0.0
  pawnStructure: boolean;
  boardControl: boolean;
}

// Tertiary settings interface for engine internals
export interface TertiarySettings {
  quiescenceDepth: number;    // Max quiescence depth (16-40)
  aspirationWindow: number;   // Aspiration window size (30-100)
  lmrThreshold: number;       // LMR move threshold (2-6)
  nullMoveR: number;          // Null move reduction R (2-3)
  ttSizeMb: number;           // Transposition table size in MB (64-1024)
}

// Position context from Position-Aware Heuristic System (Phase 7)
export interface PositionContextData {
  game_phase: string;      // Opening, Early Middlegame, Late Middlegame, Endgame
  situation: string;       // Situation codes: OPN, MID, END, ATK, DEF, etc.
  is_emergency: boolean;   // Is this an emergency situation?
  primary_goal: string;    // Primary goal name
  goals: string;           // All active goals as comma-separated string
  profile: string;         // Profile type: ATTACK, DEFENSE, ENDGAME, DEVELOPMENT, BALANCED
}

interface SearchResponse {
  success: boolean;
  best_move: string | null;
  from: { row: number; col: number } | null;
  to: { row: number; col: number } | null;
  score: number;
  depth: number;
  nodes: number;
  time_ms: number;
  nps: number;
  threads_used: number;
  error: string | null;
  position_context?: PositionContextData;  // Position-Aware Heuristic System context
}

interface ValidateMoveRequest {
  fen: string;
  from_row: number;
  from_col: number;
  to_row: number;
  to_col: number;
  promotion?: number;
}

interface ValidateMoveResponse {
  valid: boolean;
  reason: string | null;
  is_castling: boolean;
  is_en_passant: boolean;
  is_promotion: boolean;
}

interface LegalMovesRequest {
  fen: string;
}

interface MoveData {
  from_row: number;
  from_col: number;
  to_row: number;
  to_col: number;
  promotion: number | null;
  algebraic: string;
}

interface LegalMovesResponse {
  success: boolean;
  moves: MoveData[];
  count: number;
  error: string | null;
}

interface GameStatusRequest {
  fen: string;
}

interface GameStatusResponse {
  success: boolean;
  status: string;
  in_check: boolean;
  is_checkmate: boolean;
  is_stalemate: boolean;
  is_draw: boolean;
  legal_move_count: number;
  error: string | null;
}

interface HealthResponse {
  status: string;
  version: string;
  max_threads: number;
  piece_types: number;
}

// ============================================================================
// Native Engine Client
// ============================================================================

export class NativeEngineClient {
  private baseUrl: string;
  private available: boolean = false;
  private maxThreads: number = 20;
  private healthCheckPromise: Promise<boolean> | null = null;

  constructor(baseUrl?: string) {
    // Use backend proxy to avoid CORS issues
    // Frontend -> Backend (/api/engine) -> Rust Engine
    const apiUrl = process.env.REACT_APP_API_URL || "";

    // If explicit baseUrl provided, use it; otherwise use backend proxy
    if (baseUrl) {
      let url = baseUrl.trim();
      if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
        url = `https://${url}`;
      }
      this.baseUrl = url;
    } else {
      // Use backend as proxy (avoids CORS)
      this.baseUrl = apiUrl ? `${apiUrl}/api/engine` : "";
    }

    // Diagnostic logging
    console.log(`[NativeEngine] REACT_APP_API_URL: "${apiUrl}"`);
    console.log(`[NativeEngine] Using proxy URL: "${this.baseUrl}"`);
  }

  /**
   * Check if the native engine server is available
   */
  async checkHealth(): Promise<boolean> {
    // Deduplicate concurrent health checks
    if (this.healthCheckPromise) {
      return this.healthCheckPromise;
    }

    this.healthCheckPromise = (async () => {
      // Skip if no URL configured
      if (!this.baseUrl) {
        console.log("[NativeEngine] No baseUrl configured, skipping health check");
        this.available = false;
        return false;
      }

      const healthUrl = `${this.baseUrl}/health`;
      console.log(`[NativeEngine] Checking health at: ${healthUrl}`);

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // Increased timeout

        const response = await fetch(healthUrl, {
          signal: controller.signal,
          mode: "cors",
          headers: {
            "Accept": "application/json",
          },
        });

        clearTimeout(timeoutId);

        console.log(`[NativeEngine] Health response status: ${response.status} ${response.statusText}`);
        console.log(`[NativeEngine] Health response headers:`, Object.fromEntries(response.headers.entries()));

        // Get raw text first for debugging
        const rawText = await response.text();
        console.log(`[NativeEngine] Health response body (raw): "${rawText.substring(0, 200)}"`);

        if (response.ok) {
          try {
            const data: HealthResponse = JSON.parse(rawText);
            this.available = data.status === "ok";
            this.maxThreads = data.max_threads || 8;
            console.log(
              `[NativeEngine] Server available: ${this.available}, threads: ${this.maxThreads}`
            );
            return this.available;
          } catch (parseError) {
            console.error(`[NativeEngine] Failed to parse JSON response: "${rawText}"`, parseError);
            this.available = false;
            return false;
          }
        } else {
          console.log(`[NativeEngine] Health check failed with status ${response.status}`);
          this.available = false;
          return false;
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          console.log("[NativeEngine] Health check timed out after 5 seconds");
        } else {
          console.log("[NativeEngine] Server not available:", error);
        }
        this.available = false;
      } finally {
        this.healthCheckPromise = null;
      }
      return false;
    })();

    return this.healthCheckPromise;
  }

  /**
   * Check if native engine is currently available (cached result)
   */
  isAvailable(): boolean {
    return this.available;
  }

  /**
   * Get maximum threads supported by the server
   */
  getMaxThreads(): number {
    return this.maxThreads;
  }

  /**
   * Search for the best move using parallel search
   * Now supports configurable evaluation weights and tertiary settings from AI Settings
   */
  async search(
    fen: string,
    options: {
      depth?: number;
      time_ms?: number;
      threads?: number;
      priority_order?: string[];
      // Evaluation weights from AI Settings
      weights?: EvaluationWeights;
      // Tertiary settings - Engine internals
      tertiary?: TertiarySettings;
    } = {}
  ): Promise<{
    success: boolean;
    from: Position | null;
    to: Position | null;
    score: number;
    depth: number;
    nodes: number;
    time_ms: number;
    nps: number;
    threads_used: number;
    error: string | null;
    position_context?: PositionContextData;  // Position-Aware Heuristic System context
  }> {
    if (!this.available) {
      return {
        success: false,
        from: null,
        to: null,
        score: 0,
        depth: 0,
        nodes: 0,
        time_ms: 0,
        nps: 0,
        threads_used: 0,
        error: "Native engine not available",
      };
    }

    try {
      const request: SearchRequest = {
        fen,
        depth: options.depth ?? 20,
        time_ms: options.time_ms ?? 10000,
        threads: options.threads ?? this.maxThreads,
        priority_order: options.priority_order,
      };

      // Add evaluation weights if provided
      if (options.weights) {
        request.material_weight = options.weights.material;
        request.position_weight = options.weights.position;
        request.king_safety_weight = options.weights.kingSafety;
        request.mobility_weight = options.weights.mobility;
        request.pawn_structure = options.weights.pawnStructure;
        request.board_control = options.weights.boardControl;

        console.log("[NativeEngine] Using custom weights:", {
          material: request.material_weight,
          position: request.position_weight,
          king_safety: request.king_safety_weight,
          mobility: request.mobility_weight,
          pawn_structure: request.pawn_structure,
          board_control: request.board_control,
        });
      }

      // Add tertiary settings if provided
      if (options.tertiary) {
        request.quiescence_depth = options.tertiary.quiescenceDepth;
        request.aspiration_window = options.tertiary.aspirationWindow;
        request.lmr_threshold = options.tertiary.lmrThreshold;
        request.null_move_r = options.tertiary.nullMoveR;
        request.tt_size_mb = options.tertiary.ttSizeMb;

        console.log("[NativeEngine] Using tertiary settings:", {
          quiescence_depth: request.quiescence_depth,
          aspiration_window: request.aspiration_window,
          lmr_threshold: request.lmr_threshold,
          null_move_r: request.null_move_r,
          tt_size_mb: request.tt_size_mb,
        });
      }

      console.log("[NativeEngine] Making search request to:", `${this.baseUrl}/search`);

      // Set timeout based on search time limit + buffer (30 seconds extra)
      const searchTimeout = (options.time_ms ?? 10000) + 30000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), searchTimeout);

      const response = await fetch(`${this.baseUrl}/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log("[NativeEngine] Search response status:", response.status);

      const responseText = await response.text();
      console.log("[NativeEngine] Search response raw:", responseText.substring(0, 500));

      let data: SearchResponse;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error("[NativeEngine] Failed to parse response JSON:", parseErr);
        return {
          success: false,
          from: null,
          to: null,
          score: 0,
          depth: 0,
          nodes: 0,
          time_ms: 0,
          nps: 0,
          threads_used: 0,
          error: `Invalid JSON response: ${responseText.substring(0, 100)}`,
        };
      }

      console.log("[NativeEngine] Parsed response:", {
        success: data.success,
        error: data.error,
        hasFrom: !!data.from,
        hasTo: !!data.to,
      });

      // Handle Railway's error format (when engine crashes/doesn't respond)
      // Railway returns: {"status":"error","code":502,"message":"Application failed to respond"}
      const railwayError = data as any;
      if (railwayError.status === "error" || railwayError.code === 502) {
        console.error("[NativeEngine] Railway error - engine crashed:", railwayError);
        return {
          success: false,
          from: null,
          to: null,
          score: 0,
          depth: 0,
          nodes: 0,
          time_ms: 0,
          nps: 0,
          threads_used: 0,
          error: railwayError.message || `Engine crashed (code ${railwayError.code || 502})`,
        };
      }

      // Log position context if available
      if (data.position_context) {
        console.log("[NativeEngine] Position context:", data.position_context);
      }

      return {
        success: data.success,
        from: data.from ? { row: data.from.row, col: data.from.col } : null,
        to: data.to ? { row: data.to.row, col: data.to.col } : null,
        score: data.score,
        depth: data.depth,
        nodes: data.nodes,
        time_ms: data.time_ms,
        nps: data.nps,
        threads_used: data.threads_used,
        error: data.error,
        position_context: data.position_context,
      };
    } catch (error) {
      console.error("[NativeEngine] Search error:", error);
      console.error("[NativeEngine] Error type:", typeof error);
      console.error("[NativeEngine] Error name:", (error as any)?.name);
      console.error("[NativeEngine] Error message:", (error as any)?.message);
      return {
        success: false,
        from: null,
        to: null,
        score: 0,
        depth: 0,
        nodes: 0,
        time_ms: 0,
        nps: 0,
        threads_used: 0,
        error: `Fetch failed: ${(error as any)?.message || String(error)}`,
      };
    }
  }

  /**
   * Validate a move
   */
  async validateMove(
    fen: string,
    from: Position,
    to: Position,
    promotion?: number
  ): Promise<ValidateMoveResponse> {
    if (!this.available) {
      return {
        valid: false,
        reason: "Native engine not available",
        is_castling: false,
        is_en_passant: false,
        is_promotion: false,
      };
    }

    try {
      const request: ValidateMoveRequest = {
        fen,
        from_row: from.row,
        from_col: from.col,
        to_row: to.row,
        to_col: to.col,
        promotion,
      };

      const response = await fetch(`${this.baseUrl}/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });

      return await response.json();
    } catch (error) {
      console.error("[NativeEngine] Validate error:", error);
      return {
        valid: false,
        reason: String(error),
        is_castling: false,
        is_en_passant: false,
        is_promotion: false,
      };
    }
  }

  /**
   * Get all legal moves for current position
   */
  async getLegalMoves(fen: string): Promise<{
    success: boolean;
    moves: Array<{ from: Position; to: Position; promotion?: number }>;
    error: string | null;
  }> {
    if (!this.available) {
      return {
        success: false,
        moves: [],
        error: "Native engine not available",
      };
    }

    try {
      const request: LegalMovesRequest = { fen };

      const response = await fetch(`${this.baseUrl}/moves`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });

      const data: LegalMovesResponse = await response.json();

      return {
        success: data.success,
        moves: data.moves.map((m) => ({
          from: { row: m.from_row, col: m.from_col },
          to: { row: m.to_row, col: m.to_col },
          promotion: m.promotion ?? undefined,
        })),
        error: data.error,
      };
    } catch (error) {
      console.error("[NativeEngine] Legal moves error:", error);
      return {
        success: false,
        moves: [],
        error: String(error),
      };
    }
  }

  /**
   * Get game status
   */
  async getGameStatus(fen: string): Promise<GameStatusResponse> {
    if (!this.available) {
      return {
        success: false,
        status: "unknown",
        in_check: false,
        is_checkmate: false,
        is_stalemate: false,
        is_draw: false,
        legal_move_count: 0,
        error: "Native engine not available",
      };
    }

    try {
      const request: GameStatusRequest = { fen };

      const response = await fetch(`${this.baseUrl}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });

      return await response.json();
    } catch (error) {
      console.error("[NativeEngine] Status error:", error);
      return {
        success: false,
        status: "error",
        in_check: false,
        is_checkmate: false,
        is_stalemate: false,
        is_draw: false,
        legal_move_count: 0,
        error: String(error),
      };
    }
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let nativeEngineInstance: NativeEngineClient | null = null;

/**
 * Get the singleton native engine client
 */
export function getNativeEngineClient(): NativeEngineClient {
  if (!nativeEngineInstance) {
    nativeEngineInstance = new NativeEngineClient();
  }
  return nativeEngineInstance;
}

/**
 * Initialize native engine - call this at app startup
 */
export async function initNativeEngine(): Promise<boolean> {
  const client = getNativeEngineClient();
  const available = await client.checkHealth();

  if (available) {
    console.log(
      `[NativeEngine] ✅ Connected to native engine with ${client.getMaxThreads()} threads`
    );
  } else {
    console.log("[NativeEngine] ⚠️ Native engine not available, will use TypeScript fallback");
  }

  return available;
}

/**
 * Check if native engine is available
 */
export function isNativeEngineAvailable(): boolean {
  return nativeEngineInstance?.isAvailable() ?? false;
}
